import { Popup } from "../../../../UI/Popup/Popup";
import Skeleton from "../../../MentorDashboardV2/UI/Skeleton";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import { useFetchSprHistory } from "../../services/api";
import { mapSprHistory } from "../../services/mappers/sprEntryMapper";
import styles from "./SprHistoryModal.module.css";

const COLUMNS = ["Date", "Time", "Sent by", "Role", "Preview"];
const SKELETON_ROWS = [0, 1, 2];

/**
 * "SPR History" popup (Figma "Pop-up → when View history is clicked", 1121:17357): the last
 * sends to the parent for this student + subject, newest first. Each row's preview icon opens
 * that send, exactly as the parent got it, in a new tab (`onPreview`).
 *
 * @param {string} studentId
 * @param {string} courseSection - the host course row's courseSection
 * @param {string} subjectKey - the row's `spr.subjectKey`
 * @param {Function} onPreview - receives the row's sentId
 * @param {Function} onClose
 */
const SprHistoryModal = ({
  studentId,
  courseSection,
  subjectKey,
  onPreview,
  onClose,
}) => {
  const {
    apiData,
    loading: isFetchingHistory,
    error: historyError,
    refetch,
  } = useFetchSprHistory({ studentId, courseSection, subjectKey });

  const rows = apiData ? mapSprHistory(apiData) : [];

  return (
    <Popup
      onClose={onClose}
      contentClassName={styles.popup}
      showCloseIcon={false}
    >
      <div className={styles.body}>
        <h2 className={styles.title}>SPR History</h2>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th key={column}>{column}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isFetchingHistory &&
                SKELETON_ROWS.map((key) => (
                  <tr key={key} aria-hidden="true">
                    {COLUMNS.map((column) => (
                      <td key={column}>
                        <Skeleton variant="text" width="70%" height={14} />
                      </td>
                    ))}
                  </tr>
                ))}

              {!isFetchingHistory && historyError && (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.stateCell}>
                    {historyError?.response?.data?.msg ||
                      "Couldn't load the history."}{" "}
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={() => refetch().catch(() => {})}
                    >
                      Try again
                    </button>
                  </td>
                </tr>
              )}

              {!isFetchingHistory && !historyError && rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className={styles.stateCell}>
                    No report has been sent to the parent yet.
                  </td>
                </tr>
              )}

              {!isFetchingHistory &&
                !historyError &&
                rows?.map((row) => (
                  <tr key={row.sentId}>
                    <td>{row.dateLabel}</td>
                    <td>{row.timeLabel}</td>
                    <td>{row.sentBy}</td>
                    <td>{row.role}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.previewBtn}
                        onClick={() => onPreview(row.sentId)}
                        aria-label={`Preview the report sent on ${row.dateLabel} at ${row.timeLabel}`}
                      >
                        <SvgIcons name="open_in_new" width="16" height="16" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <button type="button" className={styles.closeBtn} onClick={onClose}>
          Close
        </button>
      </div>
    </Popup>
  );
};

export default SprHistoryModal;
